import { deleteOrganizationApi } from "@/api/services/admin/organization";
import { TGetOrganizationDto } from "@/api/services/admin/organization/dtos/get-organization.dto";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import { Button } from "@/modules/irohana-components/elements/Button";
import { Tag } from "@/modules/irohana-components/elements/Tag";
import { TopPage } from "@/modules/irohana-components/elements/TopPage";
import { MenuNavigate } from "@/modules/irohana-components/elements/MenuNavigate";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { OrganizationStatusTag } from "@/features/organization/components/OrganizationStatusTag";
import { OrganizationBasicInfo } from "@/features/organization/components/OrganizationBasicInfo";
import { OrganizationContractInfo } from "@/features/organization/components/OrganizationContractInfo";
import { OrganizationOtherInfo } from "@/features/organization/components/OrganizationOtherInfo";
import { OrganizationFooterContainer } from "@/features/organization/components/OrganizationFooterContainer";
import { useDialog } from "@/hooks/useDialog";
import { useToast } from "@/hooks/useToast";
import { AppRoutes } from "@/routes/config";
import { black } from "@/theme/colors";
import { EOrganizationContractStatus } from "@/types/enum";
import { showError } from "@/utils/error";
import { Stack, styled } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FC } from "react";
import { useNavigate } from "react-router-dom";

type TOrganizationDetailPageProps = {
  organization: TGetOrganizationDto;
};

// Only cancelled organizations may be deleted (mirrors the backend guard + RSO).
const DELETABLE_STATUSES: string[] = [EOrganizationContractStatus.CANCELED_CONTRACT, EOrganizationContractStatus.TRIAL_CANCELLED];

export const OrganizationDetailPage: FC<TOrganizationDetailPageProps> = ({ organization }) => {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const confirmDeleteDialog = useDialog();

  const canDelete = DELETABLE_STATUSES.includes(organization.contractStatus) && !organization.deletedAt;

  const handleDelete = async () => {
    try {
      if (!organization?.id) return;
      const agreeToDelete = await confirmDeleteDialog.show();
      if (!agreeToDelete) return;

      const rs = await deleteOrganizationApi(String(organization.id));
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      navigate(AppRoutes.organizationList);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATION.FETCH_LIST] });
    } catch (error) {
      showError(error);
    }
  };

  return (
    <DetailWrapper gap={3}>
      <TopPage
        title={organization.name}
        tooltip={<OrganizationStatusTag status={organization.contractStatus} />}
        enablePageBack={false}
        rightContent={
          <Stack direction="row" alignItems="center" gap={2}>
            {organization.deletedAt && <Tag variant="bold" title="削除済み" color={black[95]} />}
            {canDelete && (
              <Button variant="danger" onClick={handleDelete}>
                削除
              </Button>
            )}
          </Stack>
        }
      />
      <MenuNavigate
        contents={[
          { title: "基本情報", content: <OrganizationBasicInfo /> },
          { title: "契約情報", content: <OrganizationContractInfo /> },
          { title: "その他情報", content: <OrganizationOtherInfo /> },
        ]}
        contentAffix={<OrganizationFooterContainer organizationId={String(organization.id)} />}
      />
      <ConfirmDeleteModal
        open={confirmDeleteDialog.open}
        onClose={confirmDeleteDialog.cancel}
        onOk={confirmDeleteDialog.confirm}
        message="組織を削除してよろしいですか？"
      />
    </DetailWrapper>
  );
};

const DetailWrapper = styled(Stack)`
  width: 1198px;
  margin: 0 auto;
`;
